import { useCallback, useEffect, useRef, useState } from 'react';
import { guidanceAccessibilityManager } from '../../utils/accessibilityUtils';
import { logger } from '../../utils/logger';
import type {
  GuideStep,
  HighlightPositionState,
  TooltipPosition,
  TooltipPositionState,
} from './types';

interface UseGuidePositioningParams {
  isActive: boolean;
  currentStep: number;
  currentGuideStep: GuideStep | undefined;
  isLastStep: boolean;
  onStepChange: (step: number) => void;
  onComplete: () => void;
  onError?: (error: Error, stepId: string) => void;
}

interface UseGuidePositioningResult {
  tooltipRef: React.RefObject<HTMLDivElement | null>;
  targetElement: HTMLElement | null;
  tooltipPosition: TooltipPositionState;
  highlightPosition: HighlightPositionState;
  isPositioning: boolean;
  setStepStartTime: (time: number) => void;
}

export const useGuidePositioning = ({
  isActive,
  currentStep,
  currentGuideStep,
  isLastStep,
  onStepChange,
  onComplete,
  onError,
}: UseGuidePositioningParams): UseGuidePositioningResult => {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPositionState>({
    top: 0,
    left: 0,
    position: 'bottom',
  });
  const [highlightPosition, setHighlightPosition] = useState<HighlightPositionState>({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const [isPositioning, setIsPositioning] = useState(false);
  const [, setStepStartTime] = useState<number>(Date.now());
  const tooltipRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const repositionTimeoutRef = useRef<number | null>(null);
  const accessibilityInitialized = useRef<boolean>(false);

  // Calculate highlight position - uses viewport coordinates since overlay is fixed
  const calculateHighlightPosition = useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const padding = 8;

    setHighlightPosition({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  }, []);

  const calculateTooltipPosition = useCallback(
    (element: HTMLElement) => {
      if (!tooltipRef.current || !currentGuideStep) return;

      setIsPositioning(true);

      try {
        const rect = element.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const spacing = 16;

        // Calculate all possible positions
        const positionCalculations = {
          top: {
            top: rect.top + scrollTop - tooltipRect.height - spacing,
            left: rect.left + scrollLeft + (rect.width - tooltipRect.width) / 2,
            fits: rect.top > tooltipRect.height + spacing,
          },
          bottom: {
            top: rect.bottom + scrollTop + spacing,
            left: rect.left + scrollLeft + (rect.width - tooltipRect.width) / 2,
            fits: rect.bottom + tooltipRect.height + spacing < viewportHeight,
          },
          left: {
            top: rect.top + scrollTop + (rect.height - tooltipRect.height) / 2,
            left: rect.left + scrollLeft - tooltipRect.width - spacing,
            fits: rect.left > tooltipRect.width + spacing,
          },
          right: {
            top: rect.top + scrollTop + (rect.height - tooltipRect.height) / 2,
            left: rect.right + scrollLeft + spacing,
            fits: rect.right + tooltipRect.width + spacing < viewportWidth,
          },
        };

        let selectedPosition: TooltipPosition;
        let finalTop: number;
        let finalLeft: number;

        if (currentGuideStep.position === 'auto') {
          // Smart auto-positioning: prefer positions that fit completely
          const fittingPositions = Object.entries(positionCalculations)
            .filter(([, calc]) => calc.fits)
            .map(([pos]) => pos as TooltipPosition);

          if (fittingPositions.length > 0) {
            // Prefer bottom, then top, then right, then left
            const preferenceOrder: TooltipPosition[] = ['bottom', 'top', 'right', 'left'];
            selectedPosition =
              preferenceOrder.find((pos) => fittingPositions.includes(pos)) || fittingPositions[0];
          } else {
            // No position fits perfectly, choose the one with most space
            selectedPosition = 'bottom'; // Default fallback
          }
        } else {
          selectedPosition = currentGuideStep.position;
        }

        const calc = positionCalculations[selectedPosition as keyof typeof positionCalculations];
        finalTop = calc.top;
        finalLeft = calc.left;

        // Ensure tooltip stays within viewport bounds
        const maxLeft = viewportWidth - tooltipRect.width - spacing;
        const maxTop = viewportHeight + scrollTop - tooltipRect.height - spacing;

        finalLeft = Math.max(spacing, Math.min(finalLeft, maxLeft));
        finalTop = Math.max(scrollTop + spacing, Math.min(finalTop, maxTop));

        // If tooltip would be off-screen horizontally, center it
        if (finalLeft <= spacing || finalLeft >= maxLeft) {
          finalLeft = (viewportWidth - tooltipRect.width) / 2;
        }

        setTooltipPosition({
          top: finalTop,
          left: finalLeft,
          position: selectedPosition,
        });
      } catch (error) {
        logger.error('[DashboardGuide] Error calculating position', { error });

        // Fallback to center of screen
        setTooltipPosition({
          top:
            window.innerHeight / 2 -
            150 +
            (window.pageYOffset || document.documentElement.scrollTop),
          left: (window.innerWidth - 480) / 2,
          position: 'bottom',
        });

        if (onError) {
          onError(
            error instanceof Error ? error : new Error('Positioning error'),
            currentGuideStep.id,
          );
        }
      } finally {
        setIsPositioning(false);
      }
    },
    [currentGuideStep, onError],
  );

  // Enhanced target element finding with error handling
  const findTargetElement = useCallback(() => {
    if (!currentGuideStep) return;

    try {
      const element = document.querySelector(currentGuideStep.target) as HTMLElement;
      if (element) {
        setTargetElement(element);

        // Immediately calculate highlight position for the new target
        calculateHighlightPosition(element);

        // Call step enter callback
        if (currentGuideStep.onStepEnter) {
          currentGuideStep.onStepEnter();
        }

        // Set up resize observer for responsive positioning
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }

        resizeObserverRef.current = new ResizeObserver(() => {
          if (repositionTimeoutRef.current) {
            window.clearTimeout(repositionTimeoutRef.current);
          }
          repositionTimeoutRef.current = window.setTimeout(() => {
            calculateHighlightPosition(element);
            calculateTooltipPosition(element);
          }, 100);
        });

        resizeObserverRef.current.observe(element);
        resizeObserverRef.current.observe(document.body);

        // Focus management and accessibility setup
        if (tooltipRef.current) {
          previousFocusRef.current = document.activeElement as HTMLElement;

          // Initialize accessibility features
          if (!accessibilityInitialized.current) {
            guidanceAccessibilityManager.initialize(tooltipRef.current, {
              focusManagement: {
                trapFocus: true,
                restoreFocus: true,
                initialFocus: 'button',
              },
              keyboardShortcuts: {
                next: ['ArrowRight', 'Enter', ' '],
                previous: ['ArrowLeft'],
                skip: ['s', 'S'],
                dismiss: ['Escape'],
              },
            });
            accessibilityInitialized.current = true;
          }

          tooltipRef.current.focus();
        }

        setStepStartTime(Date.now());
      } else {
        // Target not found - handle gracefully
        const error = new Error(`Guide target not found: ${currentGuideStep.target}`);
        logger.warn('[DashboardGuide] Target not found', { target: currentGuideStep.target });

        if (onError) {
          onError(error, currentGuideStep.id);
        }

        // Auto-advance to next step if target is missing
        setTimeout(() => {
          if (isLastStep) {
            onComplete();
          } else {
            onStepChange(currentStep + 1);
          }
        }, 1000);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error finding target');
      logger.error('[DashboardGuide] Error finding target', { error: err });

      if (onError) {
        onError(err, currentGuideStep.id);
      }
    }
  }, [
    currentGuideStep,
    currentStep,
    isLastStep,
    onComplete,
    onStepChange,
    onError,
    calculateTooltipPosition,
    calculateHighlightPosition,
  ]);

  useEffect(() => {
    if (!isActive || !currentGuideStep) return;

    // Add body class to prevent layout shifts
    document.body.classList.add('guide-active');

    // Small delay to ensure DOM is ready
    const timer = setTimeout(findTargetElement, 100);
    return () => {
      clearTimeout(timer);
      if (repositionTimeoutRef.current) {
        window.clearTimeout(repositionTimeoutRef.current);
      }
      // Remove body class
      document.body.classList.remove('guide-active');
    };
  }, [isActive, currentStep, currentGuideStep, findTargetElement]);

  // Cleanup and focus restoration
  useEffect(() => {
    return () => {
      // Cleanup accessibility features
      if (accessibilityInitialized.current) {
        guidanceAccessibilityManager.cleanup();
        accessibilityInitialized.current = false;
      }

      // Cleanup resize observer
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      // Clear timeouts
      if (repositionTimeoutRef.current) {
        window.clearTimeout(repositionTimeoutRef.current);
      }

      // Restore focus
      if (previousFocusRef.current && document.contains(previousFocusRef.current)) {
        previousFocusRef.current.focus();
      }

      // Call step exit callback
      if (currentGuideStep?.onStepExit) {
        currentGuideStep.onStepExit();
      }
    };
  }, [isActive, currentGuideStep]);

  // Recalculate position when tooltip content changes
  useEffect(() => {
    if (targetElement && tooltipRef.current && !isPositioning) {
      const timer = setTimeout(() => {
        calculateHighlightPosition(targetElement);
        calculateTooltipPosition(targetElement);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [
    targetElement,
    currentGuideStep,
    calculateTooltipPosition,
    calculateHighlightPosition,
    isPositioning,
  ]);

  // Update positions on scroll and window resize
  useEffect(() => {
    if (!isActive || !targetElement) return;

    const updatePositions = () => {
      calculateHighlightPosition(targetElement);
      calculateTooltipPosition(targetElement);
    };

    // Throttle scroll updates for performance
    let scrollTimeout: number | null = null;
    const handleScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = window.setTimeout(() => {
        updatePositions();
        scrollTimeout = null;
      }, 16); // ~60fps
    };

    // Debounce resize updates
    let resizeTimeout: number | null = null;
    const handleResize = () => {
      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout);
      }
      resizeTimeout = window.setTimeout(updatePositions, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    // Also listen to scroll on any scrollable ancestor
    let scrollableParent: HTMLElement | null = targetElement.parentElement;
    while (scrollableParent) {
      if (scrollableParent.scrollHeight > scrollableParent.clientHeight) {
        scrollableParent.addEventListener('scroll', handleScroll, { passive: true });
      }
      scrollableParent = scrollableParent.parentElement;
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (scrollTimeout) window.clearTimeout(scrollTimeout);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);

      // Clean up parent scroll listeners
      scrollableParent = targetElement.parentElement;
      while (scrollableParent) {
        scrollableParent.removeEventListener('scroll', handleScroll);
        scrollableParent = scrollableParent.parentElement;
      }
    };
  }, [isActive, targetElement, calculateHighlightPosition, calculateTooltipPosition]);

  return {
    tooltipRef,
    targetElement,
    tooltipPosition,
    highlightPosition,
    isPositioning,
    setStepStartTime,
  };
};
